import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { Router } from "@angular/router";
import {
  GetContextMenuItemsParams,
  ColDef,
  IDatasource,
  IGetRowsParams,
  MenuItemDef,
} from "ag-grid-community";

import { AuthenticationService } from "src/app/auth/authentication.service";
import { PermissionService } from "src/app/auth/permission.service";
import { ButtonRendererComponent } from "src/app/renderer/button-renderer.component";
import { TableHeaderToolTipComponent } from "src/app/shared/table-header-tool-tip/table-header-tool-tip.component";
import {
  PERSISTANT_ITEM,
  ServerRequest,
} from "src/app/_models/global.data.model";
import { SettingService } from "src/app/_services/admin/setting.service";
import { DeleteRecordService } from "src/app/_services/delete-record.service";
import { DynamicFormService } from "src/app/_services/dynamic-form.service";
import { ProductService } from "src/app/_services/product.service";
import { UtilServiceService } from "src/app/_services/utilService/util-service.service";
import { ErrorUtil } from "src/app/_utilities/error";
import { SubscriptionUtil } from "src/app/_utilities/subscription";
import { Global } from "src/app/_utilities/global";
import { DynamicTableColumnsService } from "src/app/_services/dynamic-table-column.service";
import { LanguageTranslateService } from "src/app/_services/language-translate.service";

var translatePjct: any;

@Component({
  selector: "app-product-table",
  templateUrl: "./product-table.component.html",
  styleUrls: ["./product-table.component.scss"],
  standalone: false,
})
export class ProductTableComponent
  extends SubscriptionUtil
  implements OnInit, OnDestroy, OnChanges
{
  @Output() noDataEvent = new EventEmitter();

  @Input() displayAction: any = false;
  @Input() searchValue: any = null;
  @Input() translate: any = null;
  @Input() filterData: any;
  @Input() lookupFilter: any = "";
  @Input() binId: any = null;

  gridApi: any;
  gridColumnApi: any;
  columnDefs: any = [];
  defaultColDef: any;
  frameworkComponents: any;
  gridParams: any;
  startFrom: any;
  product: any;
  pageSize: any = 25;
  parameters: any;
  gridOptions: any;
  overlayNoRowsTemplate!: string;

  assets: any;
  actionData: any;
  totalRecord: any;
  currentRecordNumber: any;
  noProducts: boolean = false;
  noProductsDataIcon: boolean = false;
  dateFormat: any;
  currency: any;
  deleteSubHasPushed = false;
  loadTable = 0;
  sortBy: any;
  sortOrder: any;
  addonFeature: any;

  // ✅ Header ID map - used by Global.setHeaderIds()
  private readonly headerIdMap: { [colId: string]: string } = {
    'productNumber': 'Tbl_HeadProdNo',
  };

  constructor(
    private productService: ProductService,
    private auth: AuthenticationService,
    private dynamicFormService: DynamicFormService,
    private router: Router,
    private errorUtil: ErrorUtil,
    private deleteRecordService: DeleteRecordService,
    private perm: PermissionService,
    private util: UtilServiceService,
    private settingService: SettingService,
    private dynamicTableColumnsService: DynamicTableColumnsService,
    private languageTranslateService: LanguageTranslateService
  ) {
    super();
    this.preInit();
  }

  ngOnInit() {
    this.addonFeature = this.auth.getAddonFeatureLists();
    this.getLanguageData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes &&
      changes['searchValue'] &&
      changes['searchValue'].previousValue !== changes['searchValue'].currentValue &&
      changes['searchValue'].currentValue.length > 2
    ) {
      this.onGridReady(this.gridParams);
    }

    if (
      changes &&
      changes['searchValue'] &&
      changes['searchValue'].previousValue !== changes['searchValue'].currentValue &&
      changes['searchValue'].currentValue === ""
    ) {
      this.onGridReady(this.gridParams);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  /******************************************** Pre Init ******************************************/
  private preInit() {
    this.frameworkComponents = {
      buttonRenderer: ButtonRendererComponent,
    };

    this.gridOptions = {
      cacheBlockSize: 25,
      rowModelType: "infinite",
      sortingOrder: ["desc", "asc"],
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      // ✅ Set header IDs after first data render
      onFirstDataRendered: () => {
        Global.setHeaderIds(this.headerIdMap);
      },
      tooltipShowDelay: 100,
    };

    this.defaultColDef = {
      enableRowGroup: false,
      enablePivot: false,
      enableValue: true,
      sortable: true,
      resizable: true,
      filter: false,
      tooltipComponent: TableHeaderToolTipComponent,
    };

    this.actionData = {
      headerName: this.translate?.general.buttons.action.singular,
      filter: false,
      sortable: false,
      width: 150,
      headerTooltip: "#",
      headerClass: "marging-auto hide-action-border",
      cellStyle: { "text-align": "center" },
      cellRenderer: "buttonRenderer",
      cellRendererParams: {
        onClick: this.edit.bind(this),
        permissions: {
          update: this.perm.capable("products", "update"),
          remove: this.perm.capable("products", "remove"),
        },
        hidden: {
          update: !this.perm.capable("products", "update"),
          remove: !this.perm.capable("products", "remove"),
        },
      },
    };
  }

  /********************************** Init Table Columns *****************************/
  private preInitTableColumn() {
    this.dateFormat = localStorage.getItem("date_format");

    this.dynamicFormService.getTableColumn("product").subscribe((res: any) => {
      this.sortBy = res.sortBy;
      this.sortOrder = res.sortOrder;

      const columnDefs: ColDef[] = [];

      res.tableColumnSetups.forEach((element: any) => {
        this.cellRenderer(element);
        columnDefs.push(element);
      });

      this.overlayNoRowsTemplate =
        "<div class='not-found'><span>" +
        this.translate?.general.messages.noRecordsFound +
        "</span> </div>";

      if (this.displayAction) {
        columnDefs.push(this.actionData);
      }

      this.loadTable = 1;

      // ✅ AG Grid v35: use setGridOption instead of setColumnDefs
      if (this.gridApi) {
        this.gridApi.setGridOption("columnDefs", columnDefs);
        this.gridApi.refreshHeader();
        setTimeout(() => {
          this.gridApi.sizeColumnsToFit();
          // ✅ Re-apply IDs after columns load
          Global.setHeaderIds(this.headerIdMap);
        }, 0);
      }

      this.onGridReady(this.gridParams);
    });
  }

  /********************************** Render Cell *****************************/
  private cellRenderer(element: any) {
    console.log(element, "asdasdasdad");

    var currentJobOrder = this.util.getPersistantItem(PERSISTANT_ITEM.JOB_ORDER);
    console.log("currentJobOrder", currentJobOrder);

    if (this.addonFeature.addonPRICEBOOK == 'PRICE_BOOK' && currentJobOrder) {
      if (currentJobOrder.jobOrder.priceBook && element.field == "price") {
        element.field = "contractPrice";
      }
    }

    var dateFormat: any = localStorage.getItem("date_format");

    if (element.translatePath) {
      element.headerName = this.languageTranslateService.getNestedValue(
        this.translate,
        element.translatePath
      );
    }

    element.headerTooltip = "#";

    if (element.field === "productNumber") {
      // ✅ No headerComponentParams - ID set dynamically via Global.setHeaderIds()
      element.cellRenderer = function (params: any) {
        if (params.data) {
          return Global.setCellRenderer(params, params.data.rowId, params.data.productNumber);
        }
      };
    } else if (element.field === "warranty") {
      element.cellRenderer = function (params: any) {
        if (params.data) {
          return params.data.warranty
            ? params.data.warranty + " month(s)"
            : "0 month(s)";
        }
      };
    } else if (element.field === "active") {
      element.cellRenderer = function (params: any) {
        if (params.data) {
          if (params.data.active === false) {
            return (
              '<span class="p-mr-2 p-tag p-component p-tag-danger"><span class="p-tag-value text-dark">' +
              translatePjct.general.status.inactive.singular +
              '</span></span>'
            );
          } else {
            return (
              '<span class="p-mr-2 p-tag p-component p-tag-success"><span class="p-tag-value">' +
              translatePjct.general.status.active.singular +
              '</span></span>'
            );
          }
        }
      };
    } else if (element.field == "price") {
      element.valueFormatter = this.currencyFormatter.bind(this);
    } else if (element.field == "contractPrice") {
      element.valueFormatter = this.currencyFormatter.bind(this);
    } else if (
      element.dataType == 'date' ||
      element.dataType == "text" ||
      element.dataType == 'datetime' ||
      element.dataType == 'boolean'
    ) {
      element = Global.setColumnByDataType(element, this.auth.getUserTimezone(), dateFormat);
    }
  }

  /******************************************** Format Currency ******************************************/
  private currencyFormatter(params: any) {
    console.log("currencyFormatter", this.currency);
    var usdFormate = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
    });
    return `${this.currency}${usdFormate.format(params.value)}`;
  }

  /********************************** Click Cell Event / Edit - Delete *****************************/
  private edit(e: any) {
    if (e.action === "edit") {
      this.util.setPersistantItem(PERSISTANT_ITEM.PRODUCT, e.rowData);
      this.router.navigate(["/dashboard/products/update"], {
        queryParams: { id: e.rowData.id },
      });
    } else if (e.action === "delete") {
      e.rowData.deleteHeader = this.translate.product.label.singular;
      e.rowData.deleteType = "Product";
      e.rowData.message =
        this.translate.general.messages.confirmDelete +
        " " +
        e.rowData.productNumber +
        " - " +
        e.rowData.name +
        "?";

      if (!this.deleteSubHasPushed) {
        this.push(
          this.deleteRecordService.getItems(e.rowData).subscribe((data) => {
            if (data.status === 200) {
              this.onRefreshList();
            }
          })
        );
        this.deleteSubHasPushed = true;
      } else {
        this.deleteRecordService.getItems(e.rowData);
      }
    }
  }

  /********************************** Refresh Data **************************/
  public onRefreshList() {
    this.onGridReady(this.gridParams);
  }

  /********************************** Search Data **************************/
  public quickSearch() {
    this.onGridReady(this.gridParams);
  }

  /********************************** On Grid Ready **************************/
  public onGridReady(params: any) {
    if (params != undefined) {
      this.gridParams = params;
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();

      // ✅ AG Grid v35: use IDatasource with IGetRowsParams
      const dataSource: IDatasource = {
        getRows: (dsParams: IGetRowsParams) => {
          if (this.loadTable === 1 && this.translate) {
            this.getServerSideData(dsParams as any);
          }
        },
      };

      this.gridApi.sizeColumnsToFit();
      // ✅ AG Grid v35: use setGridOption instead of setDatasource
      this.gridApi.setGridOption("datasource", dataSource);
    }
  }

  /********************************** Call API to Display data **************************/
  private getServerSideData(agGridGetRowsParams: any) {
    this.gridApi.hideOverlay();
    const serverRequest = this.getRequestParams(agGridGetRowsParams);
    var count: any = serverRequest.startRow;
    var priceBookId = null;
    var url = null;
    var currentJobOrder = this.util.getPersistantItem(PERSISTANT_ITEM.JOB_ORDER);
    console.log("currentJobOrder", currentJobOrder);

    if (this.addonFeature.addonPRICEBOOK == 'PRICE_BOOK' && currentJobOrder) {
      if (currentJobOrder.jobOrder.priceBook) {
        url = '/products/price-book-product';
        priceBookId = currentJobOrder.jobOrder.priceBook
          ? currentJobOrder.jobOrder.priceBook.id
          : null;
      }
    }

    let activeFlag: boolean = true;

    this.push(
      this.productService
        .getAllProductsV2(this.searchValue, serverRequest, null, url, priceBookId, this.binId, activeFlag)
        .subscribe(
          (data: any) => {
            this.assets = data.body.data;
            this.totalRecord = data.body.total;
            this.assets.forEach((element: any) => {
              count++;
              element.rowId = 'Tbl_RecProdNo' + count;
              element.buttonEdit = 'Btn_ProdEdit' + count;
              element.buttonDelete = 'Btn_ProdDel' + count;
            });
            this.setGridData(agGridGetRowsParams, this.assets, data.body.total, data.body.status);
          },
          (err) => {
            this.errorUtil.setErrorMessage(err);
          }
        )
    );
  }

  /********************************** Set Data for Grid Table **************************/
  private setGridData(
    agGridGetRowsParams: any,
    resultItems: any[],
    totalCount: number,
    status: string
  ) {
    if (status === "NO_DATA") {
      this.noProducts = false;
      this.noProductsDataIcon = true;
    } else {
      this.noProducts = true;
      this.noProductsDataIcon = false;

      if (this.assets.length === 0) {
        this.gridApi.showNoRowsOverlay();
      } else {
        // ✅ AG Grid v35: use successCallback for infinite row model
        agGridGetRowsParams.successCallback(this.assets, totalCount);
      }

      if (this.noProducts) {
        this.noDataEvent.emit(this.noProducts);
      }
    }
  }

  /********************************** Request Parameter for API **************************/
  private getRequestParams(agGridRequest: any): ServerRequest {
    if (agGridRequest.sortModel.length <= 0) {
      this.parameters = { colId: this.sortBy, sort: this.sortOrder };
      agGridRequest.sortModel.push(this.parameters);
    }

    this.currentRecordNumber = agGridRequest.startRow;
    return {
      startRow: agGridRequest.startRow,
      pageSize: 25,
      filterModel: null,
      sortModel: agGridRequest.sortModel,
    };
  }

  /********************************** View Single Record **************************/
  public viewProduct(e: any) {
    if (!this.filterData) {
      this.util.resetTabView();
      if (e.colDef.field === "productNumber" && e.colDef.cellClass !== null) {
        this.router.navigate(["dashboard/products/view/" + e.data.id], {
          queryParams: { id: e.data.id, serialNo: null },
        });
      } else {
        this.dynamicTableColumnsService.redirectURL(e);
      }
    } else {
      this.productService.setItems(e.data);
    }
  }

  /********************************** Get Context Menu **************************/
  public getContextMenuItems(
    params: GetContextMenuItemsParams
  ): (string | MenuItemDef)[] {
    this.util.resetTabView();

    var tabData: any | null = params;

    if (tabData.column.colId == "productNumber") {
      var result: (string | MenuItemDef)[] = [
        {
          name: "Open link in new tab",
          action: () => {
            var url: any = this.router.serializeUrl(
              this.router.createUrlTree(
                ["/dashboard/products/view" + tabData.id],
                { queryParams: { id: tabData.id } }
              )
            );
            url = url.replace("%23", "#");
            window.open(url, "_blank");
          },
          cssClasses: ["redFont", "bold"],
        },
      ];
      return result;
    } else {
      return [];
    }
  }

  /******************************************** Get Language Data ******************************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
          translatePjct = data;
          this.settingService.getOrgSettings(1).subscribe((res: any) => {
            this.currency = res.currency;
            this.preInitTableColumn();
          });
        }
      })
    );
    console.log("translate", this.translate);
  }

  /******************************************** Add All the Subscriptions ******************************************/
  push(obs: any) {
    super.push(obs);
  }
}